import posthog from "posthog-js";
import { normalizePathname } from "@/lib/analytics/posthog-session-replay-paths";
import type {
  ListingImageValidationIssue,
  ListingImageValidationResult,
} from "@/lib/listingImageValidation";
import type {
  ListingPhotoCoachSettings,
  ListingPhotoGateMode,
} from "@/lib/listingPhotoCoachSettings";

/** Client PostHog events for listing photo coach gates (block / warn). */

export const LISTING_PHOTO_BLOCKED_EVENT = "listing_photo_blocked" as const;
export const LISTING_PHOTO_WARNED_EVENT = "listing_photo_warned" as const;

export type ListingPhotoAnalyticsSurface = "sell" | "edit" | "admin" | "other";

export type ListingPhotoGateKind = "dimension" | "quality" | "file";

type CaptureListingPhotoFrictionParams = {
  validation: ListingImageValidationResult;
  settings: ListingPhotoCoachSettings;
  /** Override pathname-derived surface (tests / special hosts). */
  surface?: ListingPhotoAnalyticsSurface;
  pathname?: string | null;
};

function capture(event: string, properties: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties);
  } catch {
    // Never block upload UX on analytics
  }
}

/** Map App Router pathname → sell / edit / admin / other. */
export function resolveListingPhotoAnalyticsSurface(
  pathname: string | null | undefined,
): ListingPhotoAnalyticsSurface {
  const path = normalizePathname(pathname);
  if (path.startsWith("/sell/edit")) return "edit";
  if (path === "/sell" || path.startsWith("/sell/")) return "sell";
  if (path === "/admin" || path.startsWith("/admin/")) return "admin";
  return "other";
}

export function listingPhotoGateKindsFromIssues(
  issues: ListingImageValidationIssue[],
): ListingPhotoGateKind[] {
  const kinds = new Set<ListingPhotoGateKind>();
  for (const issue of issues) {
    if (issue === "too_small") kinds.add("dimension");
    else if (issue === "too_blurry" || issue === "too_dark")
      kinds.add("quality");
    else kinds.add("file");
  }
  return [...kinds];
}

function currentPathname(explicit?: string | null): string | null | undefined {
  if (explicit !== undefined) return explicit;
  if (typeof window === "undefined") return null;
  return window.location.pathname;
}

function buildFrictionProps(
  params: CaptureListingPhotoFrictionParams,
): Record<string, unknown> {
  const { validation, settings } = params;
  const surface =
    params.surface ??
    resolveListingPhotoAnalyticsSurface(currentPathname(params.pathname));
  const gates = listingPhotoGateKindsFromIssues(validation.issues);

  return {
    surface,
    issues: validation.issues,
    primary_issue: validation.issues[0] ?? null,
    gates,
    short_edge: validation.shortEdge ?? null,
    min_short_edge_px: settings.listing_photo_min_short_edge_px,
    dimension_mode: settings.listing_photo_min_dimension_mode,
    quality_mode: settings.listing_photo_quality_check_mode,
    blur_score: validation.blurScore ?? null,
    mean_brightness: validation.meanBrightness ?? null,
  };
}

/**
 * Fire once per rejected/warned file after {@link validateListingImageFile}.
 * No-op when neither blocked nor warned.
 */
export function captureListingPhotoGateResult(
  params: CaptureListingPhotoFrictionParams,
): void {
  const { validation } = params;
  if (!validation.blocked && !validation.warned) return;
  if (validation.issues.length === 0) return;

  const properties = buildFrictionProps(params);
  if (validation.blocked) {
    capture(LISTING_PHOTO_BLOCKED_EVENT, {
      ...properties,
      mode: "block" satisfies ListingPhotoGateMode,
    });
    return;
  }

  capture(LISTING_PHOTO_WARNED_EVENT, {
    ...properties,
    mode: "warn" satisfies ListingPhotoGateMode,
  });
}
